import { FormikHelpers } from "formik";
import { ReactElement } from "react";

import {
  CreateShortCodeOptInKeywordMutation,
  CreateShortCodeOptInKeywordMutationVariables,
} from "~frontend/__generated__/graphql";
import { submitErrorField } from "~frontend/components/forms/FormikErrorBanner";
import { MediaFieldValue } from "~frontend/components/forms/FormikMediaField";
import {
  CREATE_SHORT_CODE_OPT_IN_KEYWORD,
  LIST_SHORT_CODE_OPT_IN_KEYWORDS,
} from "~frontend/graphql/shortcode";
import { useMediaUpload } from "~frontend/hooks/useMediaUpload";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import KeywordDialogForm, { KeywordValues } from "./KeywordDialogForm";

export default function CreateKeywordDialog({
  open,
  onClose,
  shortcodeId,
  shortcodePhoneNumber,
}: {
  open: boolean;
  onClose: () => void;
  shortcodeId: string;
  shortcodePhoneNumber: string;
}): ReactElement {
  const [createKeyword] = useMutation<
    CreateShortCodeOptInKeywordMutation,
    CreateShortCodeOptInKeywordMutationVariables
  >(CREATE_SHORT_CODE_OPT_IN_KEYWORD, {
    refetchQueries: [
      {
        query: LIST_SHORT_CODE_OPT_IN_KEYWORDS,
        variables: { input: { shortcodeId } },
      },
    ],
    awaitRefetchQueries: true,
  });

  const getErrorMessage = useErrorMessageFormatter();

  const { uploadMedia } = useMediaUpload();

  async function onSubmit(
    values: KeywordValues,
    formik: FormikHelpers<KeywordValues>
  ) {
    try {
      const mediaUpload = await uploadMedia(values.media as MediaFieldValue);
      await createKeyword({
        variables: {
          input: {
            shortcodeId,
            keyword: values.keyword,
            additionalWelcomeMessage: values.script || null,
            mediaUploadId: mediaUpload?.id || null,
          },
        },
      });
      formik.resetForm();
      onClose();
    } catch (e) {
      formik.setErrors({
        [submitErrorField]: getErrorMessage(e),
      });
    }
  }

  return (
    <KeywordDialogForm
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      initialValues={{
        keyword: "",
        script: "",
        [submitErrorField]: null,
        media: null,
      }}
      dialogTitle={"New Opt In Keyword"}
      dialogCopy={
        "The message entered below will be sent in addition to the standard opt in welcome message that is specified in your short code contract. You can leave it blank to send just the standard welcome message."
      }
      shortcodePhoneNumber={shortcodePhoneNumber}
    />
  );
}
